import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  CheckCircle,
  XCircle,
  FileText,
  Building,
  Users,
  AlertTriangle,
  Calendar,
  ExternalLink,
  Download
} from 'lucide-react';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import CloseButton from '@components/common/CloseButton';
import { DateInput, Textarea } from '@components/common';
import { evidenceService } from '@services';
import {
  utcToLocalForInput,
  localInputToUTC,
  getNextAvailableHour,
  formatInstantAsDate
} from '@utils/dateUtils';
import { EvidenceFile, EvidenceForReview } from '@app-types';
import { priorityToVariant, evidenceStatusToVariant } from '@utils/badgeMappers';
import { getApiErrorMessage } from '@utils/apiError';
import { DRAFT_EVIDENCE_LABEL } from '@utils/evidenceVisibility';
import { getEvidenceStatusLabel } from '@utils/referenceCatalog';
import { showToast } from '@utils/toast';
import styles from './EvidenceReviewModal.module.css';

interface EvidenceReviewModalProps {
  evidence: EvidenceForReview;
  isOpen: boolean;
  onClose: () => void;
  hideAssessmentLink?: boolean;
}

const EvidenceReviewModal: React.FC<EvidenceReviewModalProps> = ({
  evidence,
  isOpen,
  onClose,
  hideAssessmentLink = false
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected' | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [notesError, setNotesError] = useState('');
  const [remediationDate, setRemediationDate] = useState<string>('');
  const [evidenceFiles, setEvidenceFiles] = useState<EvidenceFile[]>([]);

  // Load evidence files from backend (already included in evidence prop)
  useEffect(() => {
    if (evidence.files) {
      setEvidenceFiles(evidence.files);
    }
  }, [evidence.files]);

  // Auto-fill remediation date when accepting gap evidence
  useEffect(() => {
    if (reviewAction === 'approved' && evidence.process_evaluation === 'Gap' && !remediationDate) {
      setRemediationDate(getNextAvailableHour());
    }
  }, [reviewAction, evidence.process_evaluation, remediationDate]);

  // Review evidence mutation
  const reviewMutation = useMutation({
    mutationFn: ({
      evidenceId,
      status,
      notes,
      remediation
    }: {
      evidenceId: number;
      status: 'approved' | 'rejected';
      notes: string;
      remediation?: string;
    }) => evidenceService.reviewEvidence(evidenceId, status, notes, remediation),
    onSuccess: async () => {
      // Immediately refetch evidence list to ensure UI is up-to-date
      await queryClient.refetchQueries({ queryKey: ['assessorPendingEvidence'] });
      // Also refetch Contract Manager gap items in case they're viewing from assessment page
      await queryClient.refetchQueries({ queryKey: ['contractManagerGapItems'] });
      queryClient.invalidateQueries({ queryKey: ['assessment-item-evidence', evidence.assessment_item_id] });
      // Invalidate audit history for the assessment
      queryClient.invalidateQueries({ queryKey: ['audit-history', 'assessment', evidence.assessment_id] });
      // Invalidate assessment items so updated notes appear without page reload
      queryClient.invalidateQueries({
        queryKey: ['assessment-items'],
        predicate: (query) => String(query.queryKey[1]) === String(evidence.assessment_id)
      });
      // Close modal and reset form
      handleClose();
    },
    onError: (error: unknown) => {
      console.error('Error reviewing evidence:', error);
      showToast.error(getApiErrorMessage(error, 'Failed to submit review'));
    }
  });

  const handleClose = () => {
    setReviewAction(null);
    setReviewNotes('');
    setNotesError('');
    setRemediationDate('');
    onClose();
  };

  const handleSubmitReview = () => {
    if (!reviewAction) {
      setNotesError('Please select approve or reject');
      return;
    }

    if (reviewAction === 'rejected' && !reviewNotes.trim()) {
      setNotesError('Review notes are required');
      return;
    }

    // Validate remediation date for gap evidence acceptance
    if (reviewAction === 'approved' && evidence.process_evaluation === 'Gap' && !remediationDate) {
      setNotesError('Remediation date is required when accepting gap evidence');
      return;
    }

    setNotesError('');

    reviewMutation.mutate({
      evidenceId: evidence.id,
      status: reviewAction,
      notes: reviewNotes.trim(),
      remediation: remediationDate || undefined
    });
  };

  // Download file handler - uses SAS URL from Azure Blob Storage
  const handleDownloadFile = (file: { name: string; download_url: string }) => {
    // Use download_url from backend (SAS URL valid for 4 hours)
    const link = document.createElement('a');
    link.href = file.download_url;
    link.download = file.name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getEvidenceTypeDisplay = (type: string) => {
    const types = {
      document: 'Document',
      screenshot: 'Screenshot',
      policy: 'Policy',
      procedure: 'Procedure',
      other: 'Other'
    };
    return types[type as keyof typeof types] || type;
  };

  // An Assessor may upload on the CM's behalf; name the submitter only when it is someone else.
  const submittedBySomeoneElse =
    evidence.submitted_by_id != null && evidence.submitted_by_id !== evidence.uploaded_by_id;

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} data-test-id="evidence-review-modal">
        <div className={styles.modalHeader}>
          <h2>Review Evidence</h2>
          <CloseButton onClick={handleClose} disabled={reviewMutation.isPending} size={24} />
        </div>

        <div className={styles.modalContent}>
          {/* Compact Evidence Summary */}
          <div className={styles.evidenceSummary}>
            <div className={styles.summaryMain}>
              <div className={styles.evidenceTitle}>
                <h3>{evidence.evidence_name}</h3>
                <Badge variant={evidenceStatusToVariant(evidence.status)}>
                  {evidence.status === 'draft' ? DRAFT_EVIDENCE_LABEL : getEvidenceStatusLabel(evidence.status)}
                </Badge>
              </div>
              <div className={styles.evidenceMeta}>
                <span className={styles.evidenceType}>{getEvidenceTypeDisplay(evidence.evidence_type)}</span>
                <span className={styles.separator}>•</span>
                <span className={styles.uploadInfo} data-test-id="evidence-upload-info">
                  {submittedBySomeoneElse
                    ? `Uploaded by ${evidence.uploaded_by_name} · Submitted by ${evidence.submitted_by_name} on ${formatInstantAsDate(evidence.submitted_at ?? evidence.created_at)}`
                    : `Uploaded by ${evidence.uploaded_by_name} on ${formatInstantAsDate(evidence.submitted_at ?? evidence.created_at)}`}
                </span>
              </div>
            </div>
          </div>

          {/* Gap & Context Info */}
          <div className={styles.gapContextCard}>
            <div className={styles.gapHeader}>
              <AlertTriangle size={16} />
              <span className={styles.gapTitle}>{evidence.process_name}</span>
              <Badge variant={priorityToVariant(evidence.process_priority)}>{evidence.process_priority} Priority</Badge>
            </div>
            <div className={styles.contextGrid}>
              <div className={styles.contextItem}>
                <Building size={14} />
                <span>{evidence.supplier_name}</span>
              </div>
              <div className={styles.contextItem}>
                <FileText size={14} />
                <span>{evidence.contract_number}</span>
              </div>
              {!hideAssessmentLink && (
                <div className={styles.contextItem}>
                  <Users size={14} />
                  <button
                    className={styles.assessmentLink}
                    onClick={() => navigate(`/assessments/${evidence.assessment_id}`)}
                    title="View assessment details"
                  >
                    {evidence.assessment_name}
                    <ExternalLink size={12} />
                  </button>
                </div>
              )}
              <div className={styles.contextItem}>
                <Calendar size={14} />
                <span>{evidence.domain_name}</span>
              </div>
            </div>
          </div>

          {/* Evidence Details */}
          <div className={styles.evidenceDetailsCompact}>
            <div className={styles.detailRow}>
              <strong>Description:</strong> {evidence.evidence_description}
            </div>
            {evidence.notes && (
              <div className={clsx(styles.detailRow, styles.notesRow)}>
                <strong>Evidence notes:</strong> {evidence.notes}
              </div>
            )}
          </div>

          {/* Evidence Files Section */}
          {evidenceFiles.length > 0 ? (
            <div className={styles.evidenceFilesSection}>
              <h4>Uploaded Files ({evidenceFiles.length})</h4>
              <div className={styles.filesList}>
                {evidenceFiles.map((file) => (
                  <div key={`${file.name}-${file.size}`} className={styles.fileItemReview}>
                    <FileText size={20} className={styles.fileIcon} />
                    <div className={styles.fileInfo}>
                      <div className={styles.fileName}>{file.name}</div>
                      <div className={styles.fileMeta}>
                        {formatFileSize(file.size)} • {file.mime_type || 'Unknown type'}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadFile(file)}>
                      <Download size={16} />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.noFilesNotice}>
              <FileText size={20} />
              <p>No files uploaded - text evidence only</p>
            </div>
          )}

          {/* Review Section */}
          {evidence.status === 'pending' && (
            <div className={styles.reviewSection}>
              <div className={styles.sectionHeader}>
                <h3>Your Review</h3>
              </div>

              <div className={styles.reviewActions}>
                <div className={styles.actionButtons}>
                  <Button
                    variant="secondary"
                    size="lg"
                    className={clsx(reviewAction === 'approved' && styles.activeApprove)}
                    onClick={() => setReviewAction('approved')}
                    disabled={reviewMutation.isPending}
                  >
                    <CheckCircle size={20} />
                    Approve Evidence
                  </Button>

                  <Button
                    variant="secondary"
                    size="lg"
                    className={clsx(reviewAction === 'rejected' && styles.activeReject)}
                    onClick={() => setReviewAction('rejected')}
                    disabled={reviewMutation.isPending}
                  >
                    <XCircle size={20} />
                    Request More Info
                  </Button>
                </div>

                <div className={styles.reviewNotesSection}>
                  <Textarea
                    id="reviewNotes"
                    label={
                      <>
                        Review Notes{' '}
                        {reviewAction === 'rejected' && (
                          <span style={{ color: 'var(--color-error-600)', marginLeft: 'var(--space-1)' }}>*</span>
                        )}
                        {reviewAction === 'rejected' && (
                          <span className={styles.helpText}>Please explain what additional information is needed</span>
                        )}
                      </>
                    }
                    placeholder={(() => {
                      if (reviewAction === 'approved') return 'Optional: Add notes about why this evidence is sufficient...';
                      if (reviewAction === 'rejected') return 'Required: Explain what additional information or clarification is needed...';
                      return 'Select approve or request more info above, then add your review notes...';
                    })()}
                    value={reviewNotes}
                    onChange={(e) => {
                      setReviewNotes(e.target.value);
                      if (notesError) setNotesError('');
                    }}
                    rows={4}
                    disabled={reviewMutation.isPending || !reviewAction}
                    error={notesError}
                    dataTestId="review-notes"
                  />
                </div>

                {/* Remediation Date Picker - Only show for Gap evidence acceptance */}
                {reviewAction === 'approved' && evidence.process_evaluation === 'Gap' && (
                  <div className={styles.remediationDateSection}>
                    <DateInput
                      label={
                        <>
                          <Calendar size={16} style={{ display: 'inline', marginRight: '8px' }} />
                          Remediation Date *<span className={styles.helpText}>When was this gap fixed?</span>
                        </>
                      }
                      type="datetime-local"
                      value={utcToLocalForInput(remediationDate)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.value) {
                          const utcISOString = localInputToUTC(e.target.value);
                          setRemediationDate(utcISOString);
                        } else {
                          setRemediationDate('');
                        }
                      }}
                      required
                      wrapperClassName={styles.remediationDateWrapper}
                      disabled={reviewMutation.isPending}
                    />
                    <small className={styles.dateHint}>
                      Select the date when this gap was remediated (typically today or date from evidence)
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <Button variant="ghost" onClick={handleClose} disabled={reviewMutation.isPending}>
            Cancel
          </Button>

          {evidence.status === 'pending' && (
            <Button variant="primary" onClick={handleSubmitReview} disabled={reviewMutation.isPending || !reviewAction}>
              {(() => {
                if (reviewMutation.isPending) return 'Submitting...';
                if (reviewAction === 'approved') return 'Submit Approval';
                if (reviewAction === 'rejected') return 'Submit Request';
                return 'Submit Review';
              })()}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvidenceReviewModal;
